// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Tabs, Tab } from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TabPanel from '../../components/tabs/TabPanel';
import SplatAwardsTab from './SplatAwardsTab';
import SplatsDefinitionsTab from './SplatsDefinitionsTab';
import SplatImagesTab from './SplatImagesTab';
import useSplatsList from '../../hooks/useSplatsList';
import useSplatStorageImages from '../../hooks/useSplatStorageImages';
import AddSplatDialog from './AddSplatDialog';
import { normalizeCompletionSplatIds } from '../../domain/splatConstants';
import useEventDefinitionsList from '../../hooks/useEventDefinitionsList';

function defaultImageForList(allowedImages) {
  if (allowedImages?.length) {
    return allowedImages[0];
  }
  return '';
}

function emptyForm(allowedImages) {
  return {
    id: '',
    name: '',
    text: '',
    image: defaultImageForList(allowedImages),
    limit: 0,
    isActive: true,
    type: 'custom',
    overlayText: '',
    beerJourneyMap: '',
    triggerThreshold: '',
    completionSplatIds: [],
    eventId: '',
  };
}

function splatToForm(splat, allowedImages) {
  const th = splat.triggerThreshold;
  const rawImage = (splat.image ?? '').trim();
  const image = allowedImages.includes(rawImage)
    ? rawImage
    : defaultImageForList(allowedImages);
  return {
    id: splat.id,
    name: splat.name ?? '',
    text: splat.text ?? '',
    image,
    limit: splat.limit ?? 0,
    isActive: splat.isActive ?? true,
    type: splat.type ?? 'custom',
    overlayText: splat.overlayText ?? '',
    beerJourneyMap: splat.beerJourneyMap ?? '',
    triggerThreshold:
      th === undefined || th === null ? '' : th,
    completionSplatIds: normalizeCompletionSplatIds(splat.completionSplatIds),
    eventId: splat.eventId ?? '',
  };
}

/**
 * Splats management page listing splat definitions from `splat_definitions`.
 */
function Splats() {
  const { splats, loading, error, createSplat, updateSplat, deleteSplat } =
    useSplatsList();
  const { events, loading: eventsLoading } = useEventDefinitionsList();
  const {
    files: storageFiles,
    fileNames,
    imageUrlByName,
    loading: storageLoading,
    error: storageError,
    refresh: refreshStorage,
    uploadFile,
    deleteImageIfUnused,
  } = useSplatStorageImages();

  const allowedImageChoices = useMemo(
    () => [...fileNames].sort((a, b) => a.localeCompare(b)),
    [fileNames],
  );

  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState(() => emptyForm([]));
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [splatTypeFilter, setSplatTypeFilter] = useState('');
  const [splatActiveFilter, setSplatActiveFilter] = useState('');

  const validEventIdSet = useMemo(() => {
    return new Set(
      events
        .map((eventRow) => String(eventRow.id ?? '').trim())
        .filter((idValue) => idValue.length > 0),
    );
  }, [events]);

  const eventIdToName = useMemo(() => {
    const map = new Map();
    events.forEach((eventRow) => {
      const idValue = String(eventRow.id ?? '').trim();
      if (!idValue) {
        return;
      }
      const label =
        String(eventRow.name ?? '').trim().length > 0
          ? String(eventRow.name).trim()
          : idValue;
      map.set(idValue, label);
    });
    return map;
  }, [events]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (!allowedImageChoices.length) return;
    const trimmedImageChoice = (form.image ?? '').trim();
    if (allowedImageChoices.includes(trimmedImageChoice)) return;
    setForm((prev) => ({
      ...prev,
      image: defaultImageForList(allowedImageChoices),
    }));
  }, [dialogOpen, allowedImageChoices, form.image]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleSplatTypeFilterChange = useCallback((event) => {
    setSplatTypeFilter(event.target.value);
  }, []);

  const handleSplatActiveFilterChange = useCallback((event) => {
    setSplatActiveFilter(event.target.value);
  }, []);

  const handleOpenAddDialog = useCallback(() => {
    setErrorMsg('');
    setDialogMode('add');
    setForm(emptyForm(allowedImageChoices));
    setDialogOpen(true);
  }, [allowedImageChoices]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setErrorMsg('');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value, checked, type: inputType } = event.target;
    const nextValue = inputType === 'checkbox' ? checked : value;
    setForm((prev) => {
      const next = { ...prev, [name]: nextValue };
      if (name === 'type' && nextValue !== 'completion') {
        next.completionSplatIds = [];
      }
      if (name === 'type' && nextValue !== 'first_event_review') {
        next.eventId = '';
      }
      return next;
    });
  }, []);

  const handleCompletionSplatIdsChange = useCallback((ids) => {
    setForm((prev) => ({
      ...prev,
      completionSplatIds: normalizeCompletionSplatIds(ids),
    }));
  }, []);

  const handleEventIdChange = useCallback((_event, newValue) => {
    setForm((prev) => ({
      ...prev,
      eventId: newValue?.id ? String(newValue.id).trim() : '',
    }));
  }, []);

  const handleEdit = useCallback(
    (splat) => {
      setErrorMsg('');
      setDialogMode('edit');
      setForm(splatToForm(splat, allowedImageChoices));
      setDialogOpen(true);
    },
    [allowedImageChoices],
  );

  const getDeleteDisabledReason = useCallback((splat) => {
    if (Number(splat.awardedCount ?? 0) > 0) {
      return 'This splat has been awarded at least once and cannot be deleted.';
    }
    const normalizedTargetId = String(splat.id ?? '').trim();
    const refs = splats.filter((s) => {
      if (String(s.id ?? '').trim() === normalizedTargetId) return false;
      if (!Array.isArray(s.completionSplatIds)) return false;
      const normalizedList = (s.completionSplatIds ?? []).map((id) =>
        String(id ?? '').trim(),
      );
      return normalizedList.includes(normalizedTargetId);
    });
    if (refs.length) {
      const names = refs.map((s) => s.name || s.id).join(', ');
      return `Required by completion splat(s): ${names}. Remove those references before deleting.`;
    }
    return null;
  }, [splats]);

  const canDeleteSplat = useCallback(
    (splat) => getDeleteDisabledReason(splat) === null,
    [getDeleteDisabledReason],
  );

  const splatIdToName = useMemo(() => {
    const map = new Map();
    splats.forEach((s) => {
      map.set(s.id, (s.name || s.id).trim() || s.id);
    });
    return map;
  }, [splats]);

  const filteredSplatsForList = useMemo(
    () =>
      splats.filter(
        (s) =>
          (!splatTypeFilter || (s.type ?? 'custom') === splatTypeFilter) &&
          (splatActiveFilter !== 'active' || s.isActive !== false) &&
          (splatActiveFilter !== 'inactive' || s.isActive === false),
      ),
    [splats, splatTypeFilter, splatActiveFilter],
  );

  const splatsListEmptyMessage =
    splats.length > 0 && filteredSplatsForList.length === 0
      ? 'No splats match these filters.'
      : undefined;

  const handleDelete = useCallback(
    async (splat) => {
      if (!canDeleteSplat(splat)) return;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Delete splat "${splat.name || splat.id}"? This cannot be undone.`,
      );
      if (!ok) return;
      try {
        await deleteSplat(splat.id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete splat', err);
      }
    },
    [canDeleteSplat, deleteSplat],
  );

  const handleSubmitSplat = useCallback(async () => {
    const id = form.id?.trim();
    const name = form.name?.trim();
    const text = form.text?.trim();
    if (id && (id === '.' || id === '..' || id.includes('/'))) {
      setErrorMsg(
        'Document ID cannot be "." or ".." or contain "/".',
      );
      return;
    }
    if (!name || !text) {
      setErrorMsg('Please provide at least a Name and Text.');
      return;
    }

    const image = form.image?.trim();
    if (!image || !allowedImageChoices.includes(image)) {
      setErrorMsg(
        'Choose an image from the list, or upload one on the Splat Images tab.',
      );
      return;
    }

    const splatType = form.type || 'custom';
    const completionIds = normalizeCompletionSplatIds(form.completionSplatIds);
    if (splatType === 'completion' && completionIds.length < 1) {
      setErrorMsg(
        'Completion splats must include at least one required splat.',
      );
      return;
    }

    const firstEventReviewEventId = String(form.eventId ?? '').trim();
    if (splatType === 'first_event_review') {
      if (
        !firstEventReviewEventId ||
        !validEventIdSet.has(firstEventReviewEventId)
      ) {
        setErrorMsg('Select an event for this splat.');
        return;
      }
    }

    const payload = {
      id,
      name,
      text,
      image,
      limit: form.limit,
      isActive: form.isActive,
      type: splatType,
      overlayText: form.overlayText,
      beerJourneyMap: form.beerJourneyMap,
      triggerThreshold: form.triggerThreshold,
      completionSplatIds: completionIds,
      eventId: firstEventReviewEventId,
    };

    setSubmitting(true);
    setErrorMsg('');
    try {
      if (dialogMode === 'edit') {
        await updateSplat(form.id, payload);
      } else {
        await createSplat(payload);
      }
      setDialogOpen(false);
      setForm(emptyForm(allowedImageChoices));
    } catch (err) {
      const message =
        err?.message ===
        'Document ID already exists. Choose a different ID.'
          ? err.message
          : dialogMode === 'edit'
            ? 'Failed to save splat. Please try again.'
            : 'Failed to create splat. Please try again.';
      setErrorMsg(message);
    } finally {
      setSubmitting(false);
    }
  }, [
    allowedImageChoices,
    createSplat,
    dialogMode,
    form,
    updateSplat,
    validEventIdSet,
  ]);

  return (
    <DashboardLayout title="Splats">
      <Container fixed>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Splats section tabs"
          >
            <Tab
              id="simple-tab-0"
              aria-controls="simple-tabpanel-0"
              label="Splat Awards"
            />
            <Tab
              id="simple-tab-1"
              aria-controls="simple-tabpanel-1"
              label="Splat Definitions"
            />
            <Tab
              id="simple-tab-2"
              aria-controls="simple-tabpanel-2"
              label="Splat Images"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <SplatAwardsTab splats={splats} loading={loading} error={error} />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SplatsDefinitionsTab
            loading={loading}
            error={error}
            splatTypeFilter={splatTypeFilter}
            onSplatTypeFilterChange={handleSplatTypeFilterChange}
            splatActiveFilter={splatActiveFilter}
            onSplatActiveFilterChange={handleSplatActiveFilterChange}
            onOpenAdd={handleOpenAddDialog}
            splats={filteredSplatsForList}
            emptyMessage={splatsListEmptyMessage}
            splatIdToName={splatIdToName}
            eventIdToName={eventIdToName}
            imageUrlByName={imageUrlByName}
            onEdit={handleEdit}
            onDelete={handleDelete}
            canDeleteSplat={canDeleteSplat}
            getDeleteDisabledReason={getDeleteDisabledReason}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <SplatImagesTab
            files={storageFiles}
            loading={storageLoading}
            error={storageError}
            refresh={refreshStorage}
            uploadFile={uploadFile}
            deleteImageIfUnused={deleteImageIfUnused}
          />
        </TabPanel>
      </Container>
      <AddSplatDialog
        open={dialogOpen}
        mode={dialogMode}
        submitting={submitting}
        errorMsg={errorMsg}
        form={form}
        allSplats={splats}
        excludeSplatId={dialogMode === 'edit' ? form.id : ''}
        availableImages={allowedImageChoices}
        imagesLoading={storageLoading}
        eventOptions={events}
        eventsLoading={eventsLoading}
        validEventIdSet={validEventIdSet}
        onClose={handleCloseDialog}
        onChange={handleFormChange}
        onCompletionSplatIdsChange={handleCompletionSplatIdsChange}
        onEventIdChange={handleEventIdChange}
        onSubmit={handleSubmitSplat}
      />
    </DashboardLayout>
  );
}

export default Splats;
